import CircledBackButton from "@/features/Common/components/CircledBackButton";
import FullscreenLoadingIndicator from "@/features/Common/components/FullscreenLoadingIndicator";
import IconLoader from "@/features/Common/components/IconLoader";
import Panel from "@/features/Common/components/Panel";
import WatermarkIcon from "@/features/Common/components/WatermarkIcon";
import useGetMyPurchases from "@/features/Common/hooks/useGetPurchases";
import { PaymentMethod, Purchase } from "@/services/core-api-adapter";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import theme, { defaultFullscreenPageStyling } from "@/theme";
import {
  convertPxToRem,
  formatAmount,
  formatDateToReadableString,
} from "@/utils";
import {
  Divider,
  ListItem,
  Stack,
  Typography,
  Pagination,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";

interface PurchasePanelProps {
  orderNumber: string;
  createdOn: string;
  amount: string;
  onClick: () => void;
  person?: string;
  orderText: string;
  hasVoucher?: boolean;
  paymentMethod: PaymentMethod;
  consultationType?: string;
  consultationId?: string;
  discountAmount?: number;
}

function PurchasePanel({
  orderNumber,
  createdOn,
  amount,
  onClick,
  person,
  orderText,
  hasVoucher = true,
  consultationType,
  paymentMethod,
  consultationId,
  discountAmount,
}: PurchasePanelProps) {
  const { t } = useTranslation();
  return (
    <Stack
      component="button"
      aria-label={`orderNumber-${orderNumber}`}
      onClick={onClick}
      sx={{
        background: theme.palette.background.paper,
        cursor: "pointer",
        border: "none",
        width: "100%",
      }}
    >
      <Panel sx={{ width: "100%" }}>
        <Stack spacing={2}>
          <Stack
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            textAlign="left"
          >
            <Stack spacing={0.5}>
              <Typography variant="body2" fontWeight="bold" color="neutral.500">
                {formatDateToReadableString(new Date(createdOn))}
              </Typography>
              {!!consultationId ? (
                <>
                  <Typography variant="body1" fontWeight="bold">
                    {t("More.myPurchases.consultationType.title")}
                  </Typography>
                  <Typography variant="body2">{consultationType}</Typography>
                </>
              ) : (
                <>
                  <Typography variant="body1" fontWeight="bold">
                    {orderText}
                  </Typography>
                  <Typography variant="body2">{orderNumber}</Typography>
                </>
              )}
              {person && (
                <>
                  <Typography variant="body1" fontWeight="bold">
                    {t("common.person")}
                  </Typography>
                  <Typography variant="body2">{person}</Typography>
                </>
              )}
            </Stack>
            <IconLoader icon="ChevronNextIcon" />
          </Stack>

          {discountAmount &&
            isFeatureEnabled(
              FeatureToggleIdentifier.ENABLE_DISCOUNT_VOUCHERS
            ) && (
              <Stack
                direction="row"
                justifyContent="left"
                alignItems="center"
                spacing={1}
                borderRadius={1}
                padding={1}
                marginTop={2}
                sx={{
                  backgroundColor: "accent2.light",
                }}
              >
                <IconLoader
                  icon="CheckVerifiedIcon"
                  color="primary"
                  sx={{ fontSize: convertPxToRem(20) }}
                />
                <Typography
                  fontWeight="500"
                  color="neutral.700"
                  textAlign="left"
                  fontSize={12}
                >
                  {t("More.myPurchases.discount.text", {
                    amount: formatAmount(discountAmount || 0, "ZAR"),
                  })}
                </Typography>
              </Stack>
            )}

          <Divider />
          <Typography variant="h3" textAlign="left">
            {isFeatureEnabled(
              FeatureToggleIdentifier.ENABLE_ENHANCED_PAYMENT_OPTIONS
            ) ? (
              paymentMethod === PaymentMethod.CareCardVoucher ? (
                <>{t("MyPurchases.orderDetails.total.voucher")}</>
              ) : (
                <Trans
                  i18nKey="MyPurchases.orderDetails.total"
                  values={{
                    count: formatAmount(Number(amount), "ZAR"),
                  }}
                >
                  <Typography
                    component="span"
                    variant="h3"
                    fontWeight="bold"
                    color="primary"
                  />
                </Trans>
              )
            ) : !hasVoucher ? (
              <Trans
                i18nKey="MyPurchases.orderDetails.total"
                values={{
                  count: formatAmount(Number(amount), "ZAR"),
                }}
              >
                <Typography
                  component="span"
                  variant="h3"
                  fontWeight="bold"
                  color="primary"
                ></Typography>
              </Trans>
            ) : (
              <>{t("MyPurchases.orderDetails.total.voucher")}</>
            )}
          </Typography>
        </Stack>
      </Panel>
    </Stack>
  );
}

export default function MyPurchases() {
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState<number>(1);
  const pageSize = 5;

  const { t } = useTranslation();

  // Restore page state when navigating back from order summary
  useEffect(() => {
    // Check if we have a stored page number in the location state
    if (location.state && location.state.fromMyPurchasesPage) {
      setPage(location.state.fromMyPurchasesPage);
    }
  }, [location.state]);

  useEffect(() => {
    sessionStorage.setItem("myPurchasesCurrentPage", page.toString());
  }, [page]);

  useEffect(() => {
    const savedPage = sessionStorage.getItem("myPurchasesCurrentPage");
    if (savedPage && !location.state?.fromMyPurchasesPage) {
      const pageNumber = parseInt(savedPage, 10);
      if (pageNumber > 0) {
        setPage(pageNumber);
      }
    }
  }, []);

  const myPurchases = useGetMyPurchases((page - 1) * pageSize, pageSize);

  const { purchases, purchasesFetchError, isPurchasesLoading, purchasesCount } =
    myPurchases;

  const ConsultationTypeDescription: Record<string, string> = {
    ONLINE_DOCTOR_CONSULTATION: t(
      "More.myPurchases.docNurseConsultation.title"
    ),
    DIGITAL_DOCTOR_CONSULT: t("More.myPurchases.docConsultation.title"),
  };

  const shouldDisplayNoPurchasesFound =
    !purchasesFetchError &&
    !isPurchasesLoading &&
    purchases !== undefined &&
    purchases.length === 0;

  const handlePageChange = useCallback(
    (event: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
    },
    []
  );

  if (isPurchasesLoading) {
    return <FullscreenLoadingIndicator />;
  }

  function onPurchasePanelClick(purchase: any) {
    // Include the current page in the navigation state
    const navigationState = { fromMyPurchasesPage: page };

    if (!!purchase.consultationId) {
      navigate(
        `/home/get-medical-assistance/consultation?orderId=${purchase.consultationId}&orderSummary=true`,
        { state: navigationState }
      );
    } else if (purchase.voucherOrderId) {
      navigate(
        `/home/get-medical-assistance/vouchers?orderId=${purchase.voucherOrderId}`,
        { state: navigationState }
      );
    } else {
      navigate(
        `/home/get-medical-assistance/pathology?orderId=${purchase.pathologyOrderId}`,
        { state: navigationState }
      );
    }
  }

  function purchasePanelOrderText(purchase: any): string {
    if (purchase.voucherOrderId) {
      return t("MyPurchases.orderNumber.voucher.title");
    } else {
      return t("PathologyOrderSummary.orderNumber.title");
    }
  }

  function onBackButtonClick() {
    navigate("/home/more");
  }

  function getConsultationTypeFromChargeCode(value: string): string {
    if (
      value &&
      Object.prototype.hasOwnProperty.call(ConsultationTypeDescription, value)
    ) {
      return ConsultationTypeDescription[
        value as keyof typeof ConsultationTypeDescription
      ];
    }
    return "";
  }

  return (
    <Stack
      sx={{
        ...defaultFullscreenPageStyling,
      }}
      spacing={2}
    >
      <CircledBackButton showLabel={true} onClick={onBackButtonClick} />

      <Typography
        component="h1"
        variant="h2"
        sx={{
          mb: 1,
        }}
      >
        {t("MyPurchases.title")}
      </Typography>

      {!isPurchasesLoading && purchasesFetchError && (
        <Typography variant="body1" align="left">
          {t("common.somethingWentWrong")}
        </Typography>
      )}

      {!isPurchasesLoading &&
        !purchasesFetchError &&
        purchases &&
        purchases?.length > 0 && (
          <>
            <Stack
              component="ul"
              role="list"
              aria-label={t("MyPurchases.title")}
              spacing={2}
            >
              {purchases.map((purchase: Purchase, index: number) => {
                const person =
                  purchase.memberFirstName + " " + purchase.memberLastName;
                const key = `${purchase.orderNumber}-${index}-page-${page}`;
                return (
                  <ListItem
                    key={key}
                    role="listitem"
                    sx={{
                      padding: 0,
                    }}
                  >
                    <PurchasePanel
                      orderNumber={purchase.orderNumber}
                      createdOn={purchase.createdOn}
                      amount={String(purchase.amount)}
                      onClick={() => onPurchasePanelClick(purchase)}
                      person={person}
                      orderText={purchasePanelOrderText(purchase)}
                      paymentMethod={purchase.paymentMethod}
                      hasVoucher={!!purchase.voucherOrderId}
                      consultationType={getConsultationTypeFromChargeCode(
                        purchase.chargeCode
                      )}
                      consultationId={purchase.consultationId}
                      discountAmount={
                        purchase.discountVoucherDetails?.discountAmount
                      }
                    />
                  </ListItem>
                );
              })}
            </Stack>
            {purchasesCount && purchasesCount > pageSize && (
              <Stack sx={{ p: 2 }}>
                <Pagination
                  aria-label={t("MyPurchases.pagination.ariaLabel")}
                  shape="rounded"
                  color="standard"
                  count={Math.ceil(purchasesCount / pageSize)}
                  page={page}
                  onChange={handlePageChange}
                  sx={{ alignSelf: "center" }}
                />
              </Stack>
            )}
          </>
        )}

      {shouldDisplayNoPurchasesFound && (
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
          height="80vh"
          width="100%"
        >
          <WatermarkIcon
            height={130}
            width={130}
            color={theme.palette.accent2[100]}
            foreground={theme.palette.info.main}
            Icon={"TrolleyWithPlusIcon"}
          />
          <Typography variant="body1" fontWeight={600} color="neutral.light">
            {t("More.myPurchases.noPurchases")}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
